import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Overlay,
  Modal,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  PdfFrame,
  FallbackMessage,
} from './EulaPdfModal.styled';

interface EulaPdfModalProps {
  url: string;
  onClose: () => void;
}

/**
 * Full-screen modal that renders the EULA PDF inline via an iframe.
 * Falls back to a "open in new tab" link if the PDF fails to load.
 * Closes on Escape key or clicking the overlay backdrop.
 */
const EulaPdfModal: React.FC<EulaPdfModalProps> = ({ url, onClose }) => {
  const [loadError, setLoadError] = useState(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll while modal is open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prev;
    };
  }, [handleKeyDown]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  return createPortal(
    <Overlay onClick={handleOverlayClick}>
      <Modal>
        <ModalHeader>
          <ModalTitle>End User License Agreement</ModalTitle>
          <CloseButton onClick={onClose} aria-label="Close">
            &#x2715;
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {!loadError ? (
            <PdfFrame
              src={url}
              title="End User License Agreement"
              onError={() => setLoadError(true)}
            />
          ) : (
            <FallbackMessage>
              <span>Unable to display the document inline.</span>
              <a href={url} target="_blank" rel="noopener noreferrer">
                Open EULA in a new tab
              </a>
            </FallbackMessage>
          )}
        </ModalBody>
      </Modal>
    </Overlay>,
    document.body,
  );
};

export default EulaPdfModal;
